import { useState } from "react";
import { Link } from "react-router-dom";
import { type AuthUser } from "wasp/auth";
import {
  getAdminFaqCategories,
  createFaqCategory,
  updateFaqCategory,
  deleteFaqCategory,
  useQuery,
  useAction,
} from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import useTranslation from "../../../client/hooks/useTranslation";
import { useToast } from "../../../client/hooks/use-toast";
import { generateSlug } from "../../../blog/slug";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";

interface CatRow {
  id: string;
  slug: string;
  nameFr: string;
  nameEn: string;
  icon: string | null;
  sortOrder: number;
  _count?: { items: number };
}

const emptyDraft = { nameFr: "", nameEn: "", slug: "", icon: "", sortOrder: 0 };

export default function AdminHelpCategoriesPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const { toast } = useToast();
  const { data, refetch } = useQuery(getAdminFaqCategories, undefined);
  const categories: CatRow[] = (data as CatRow[]) ?? [];

  const createCategory = useAction(createFaqCategory);
  const updateCategory = useAction(updateFaqCategory);
  const deleteCategory = useAction(deleteFaqCategory);

  const [draft, setDraft] = useState({ ...emptyDraft });
  const [editingId, setEditingId] = useState<string | null>(null);

  const startNew = () => {
    setEditingId("new");
    setDraft({ ...emptyDraft, sortOrder: categories.length });
  };

  const startEdit = (c: CatRow) => {
    setEditingId(c.id);
    setDraft({ nameFr: c.nameFr, nameEn: c.nameEn, slug: c.slug, icon: c.icon ?? "", sortOrder: c.sortOrder });
  };

  const save = async () => {
    const payload = {
      nameFr: draft.nameFr,
      nameEn: draft.nameEn,
      slug: draft.slug || generateSlug(draft.nameFr),
      icon: draft.icon || undefined,
      sortOrder: Number(draft.sortOrder) || 0,
    };
    try {
      if (editingId === "new") {
        await createCategory(payload);
      } else if (editingId) {
        await updateCategory({ id: editingId, ...payload });
      }
      setEditingId(null);
      toast({ title: t.adminHelp.saved });
      refetch();
    } catch (e: any) {
      toast({ title: e?.message ?? "Error", variant: "destructive" });
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm(t.adminHelp.confirmDelete)) return;
    try {
      await deleteCategory({ id });
      toast({ title: t.adminHelp.deleted });
      refetch();
    } catch (e: any) {
      toast({ title: t.adminHelp.deleteCategoryBlocked, variant: "destructive" });
    }
  };

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={t.adminHelp.categoriesTitle} />

      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">{t.adminHelp.categoriesTitle}</h2>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/admin/help/questions">{t.adminHelp.manageQuestions}</Link>
            </Button>
            <Button onClick={startNew}>{t.adminHelp.newCategory}</Button>
          </div>
        </div>

        {editingId && (
          <div className="mb-6 grid grid-cols-1 gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
            <label className="text-sm text-muted-foreground">
              {t.adminHelp.nameFr}
              <Input value={draft.nameFr} onChange={(e) => setDraft({ ...draft, nameFr: e.target.value })} />
            </label>
            <label className="text-sm text-muted-foreground">
              {t.adminHelp.nameEn}
              <Input value={draft.nameEn} onChange={(e) => setDraft({ ...draft, nameEn: e.target.value })} />
            </label>
            <label className="text-sm text-muted-foreground">
              {t.adminHelp.slug}
              <Input
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                placeholder={generateSlug(draft.nameFr)}
              />
            </label>
            <label className="text-sm text-muted-foreground">
              {t.adminHelp.icon}
              <Input value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })} placeholder="Globe" />
            </label>
            <label className="text-sm text-muted-foreground">
              {t.adminHelp.sortOrder}
              <Input
                type="number"
                value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
              />
            </label>
            <div className="col-span-full flex gap-2">
              <Button onClick={save}>{t.adminHelp.save}</Button>
              <Button variant="ghost" onClick={() => setEditingId(null)}>
                {t.adminHelp.cancel}
              </Button>
            </div>
          </div>
        )}

        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {categories.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="font-medium text-foreground">
                  {c.nameFr} <span className="text-muted-foreground">/ {c.nameEn}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {c.slug} · {c.icon ?? "—"} ·{" "}
                  {t.adminHelp.itemCount.replace("{count}", String(c._count?.items ?? 0))} · #{c.sortOrder}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(c)}>
                  {t.adminHelp.edit}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => remove(c.id)}>
                  {t.adminHelp.delete}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </DefaultLayout>
  );
}
